/**
 * ExercicesPlateau.jsx — Page « Les 12 exercices plateau officiels ».
 *
 * Route : /enseignant/exercices-plateau
 *
 * Format : page mobile-first « Apple tactile pro » dans l'esprit des autres
 * pages enseignant (SeanceLive, CCSJuryHub, SeancesLiveHistory).
 *
 * Source : notes manuscrites du moniteur (cahier WA0000-WA0002).
 */
import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Target, ListChecks, AlertTriangle,
  ChevronDown, Printer, Notebook,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import useSeo from '../hooks/useSeo';
import {
  EXERCICES_PLATEAU, CATEGORIES_PLATEAU, GLOSSAIRE_PLATEAU,
} from '../data/teacher/exercicesPlateau';

const CRITERE_TESTID = (exId, idx) => `critere-${exId}-${idx}`;

/* ── Filtre catégorie ──────────────────────────────────────────── */
function CategoryFilter({ active, onChange }) {
  const entries = useMemo(
    () => [['all', { label: 'Tous', accent: '#1c1917' }], ...Object.entries(CATEGORIES_PLATEAU)],
    [],
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="plateau-filter">
      {entries.map(([key, info]) => {
        const selected = active === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className="rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-wider border-2 transition-colors"
            style={{
              borderColor: selected ? info.accent : 'transparent',
              background: selected ? info.accent : 'rgba(0,0,0,0.04)',
              color: selected ? 'white' : info.accent,
            }}
            data-testid={`filter-${key}`}
          >
            {info.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Carte exercice ────────────────────────────────────────────── */
function ExerciceCard({ exercice }) {
  const cat = CATEGORIES_PLATEAU[exercice.categorie] || CATEGORIES_PLATEAU['maitrise-statique'];
  const accent = cat.accent;
  const [checks, setChecks] = useState({});

  const allCriteres = useMemo(() => {
    if (exercice.sections) {
      return exercice.sections.flatMap((s, si) =>
        s.criteres.map((c, ci) => ({
          ...c, key: `s${si}-${ci}`, section: s.nom,
        })),
      );
    }
    return (exercice.criteres || []).map((c, ci) => ({
      ...c, key: `c${ci}`, section: null,
    }));
  }, [exercice]);

  const doneCount = allCriteres.filter((c) => checks[c.key]).length;
  const totalCount = allCriteres.length;
  const ratio = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const toggle = useCallback((key) => {
    setChecks((s) => ({ ...s, [key]: !s[key] }));
  }, []);

  return (
    <article
      className="rounded-2xl border-2 bg-card overflow-hidden"
      style={{ borderColor: `${accent}33` }}
      data-testid={`exercice-card-${exercice.id}`}
    >
      {/* Header */}
      <header
        className="p-4 sm:p-5"
        style={{ background: `${accent}0d` }}
      >
        <div className="flex items-start gap-3">
          {/* Badge numéro */}
          <div
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0 font-black"
            style={{ background: accent }}
          >
            <span className="text-[9px] uppercase tracking-widest opacity-80 leading-none">EX</span>
            <span className="text-xl sm:text-2xl leading-none mt-0.5 tabular-nums">{exercice.id}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-lg sm:text-xl font-black leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid={`exercice-titre-${exercice.id}`}
            >
              {exercice.titre}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-snug">
              {exercice.sousTitre}
            </p>
            {/* Méta tags */}
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {exercice.allure && (
                <span
                  className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wider"
                  style={{ background: `${accent}20`, color: accent }}
                >
                  {exercice.allure}
                </span>
              )}
              {(exercice.remcCodes || []).map((rc) => (
                <span
                  key={rc}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-black bg-muted text-muted-foreground"
                >
                  REMC · {rc}
                </span>
              ))}
              {exercice.danger && (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-red-500/15 text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-3 h-3" /> Urgence
                </span>
              )}
            </div>
          </div>
        </div>
        {/* Progression mini */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${ratio}%`, background: accent }}
            />
          </div>
          <span
            className="text-[11px] font-black tabular-nums"
            style={{ color: accent }}
            data-testid={`exercice-progress-${exercice.id}`}
          >
            {doneCount}/{totalCount}
          </span>
        </div>
      </header>

      {/* Critères */}
      <div className="px-4 sm:px-5 py-3 space-y-2">
        {exercice.variantes && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground self-center mr-1">Variantes :</span>
            {exercice.variantes.map((v) => (
              <span
                key={v}
                className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold border"
                style={{ borderColor: `${accent}33`, color: accent }}
              >
                {v}
              </span>
            ))}
          </div>
        )}
        {exercice.paliers && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground self-center mr-1">Paliers :</span>
            {exercice.paliers.map((p) => (
              <span
                key={p}
                className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold"
                style={{ background: `${accent}20`, color: accent }}
              >
                {p}
              </span>
            ))}
          </div>
        )}

        {exercice.sections ? (
          exercice.sections.map((sec, si) => (
            <div key={si} className="mt-2">
              <div
                className="text-[10px] font-black uppercase tracking-widest mb-1.5 flex items-center gap-1.5"
                style={{ color: accent }}
              >
                <ListChecks className="w-3 h-3" /> {sec.nom}
              </div>
              <ul className="space-y-1.5">
                {sec.criteres.map((c, ci) => {
                  const key = `s${si}-${ci}`;
                  return (
                    <CritereLine
                      key={key}
                      crit={c}
                      checked={!!checks[key]}
                      onToggle={() => toggle(key)}
                      accent={accent}
                      testid={CRITERE_TESTID(exercice.id, `s${si}-${ci}`)}
                    />
                  );
                })}
              </ul>
            </div>
          ))
        ) : (
          <ul className="space-y-1.5">
            {(exercice.criteres || []).map((c, ci) => {
              const key = `c${ci}`;
              return (
                <CritereLine
                  key={key}
                  crit={c}
                  checked={!!checks[key]}
                  onToggle={() => toggle(key)}
                  accent={accent}
                  testid={CRITERE_TESTID(exercice.id, ci)}
                />
              );
            })}
          </ul>
        )}
      </div>
    </article>
  );
}

/* ── Ligne critère (cliquable, checkable) ──────────────────────── */
function CritereLine({ crit, checked, onToggle, accent, testid }) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left rounded-lg p-2 -mx-2 flex items-start gap-2.5 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        data-testid={testid}
        aria-pressed={checked}
      >
        <span
          className="w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all mt-0.5"
          style={{
            borderColor: checked ? accent : 'rgb(var(--border, 200 200 200))',
            background: checked ? accent : 'transparent',
          }}
          aria-hidden="true"
        >
          {checked && (
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 8 7 12 13 4" />
            </svg>
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm font-bold leading-snug ${checked ? 'line-through opacity-60' : ''}`}
          >
            {crit.label}
          </div>
          {crit.detail && (
            <div className="text-xs text-muted-foreground leading-snug mt-0.5">
              {crit.detail}
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

/* ── Page principale ───────────────────────────────────────────── */
export default function ExercicesPlateau() {
  const [filter, setFilter] = useState('all');

  useSeo({
    title: 'Les 12 exercices plateau officiels — Espace enseignant',
    description: 'Aide-mémoire mobile pour le terrain : les 12 exercices plateau et leurs critères-clés, fidèles aux notes du moniteur. Checklist tactile + glossaire.',
    path: '/enseignant/exercices-plateau',
  });

  const filtered = useMemo(() => {
    if (filter === 'all') return EXERCICES_PLATEAU;
    return EXERCICES_PLATEAU.filter((e) => e.categorie === filter);
  }, [filter]);

  return (
    <div className="min-h-screen pb-16" data-testid="exercices-plateau-page">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button asChild variant="ghost" size="sm" className="gap-1" data-testid="btn-back">
            <Link to="/enseignant">
              <ArrowLeft className="w-4 h-4" /> Espace enseignant
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            className="gap-1.5"
            data-testid="btn-print"
          >
            <Printer className="w-4 h-4" /> Imprimer
          </Button>
        </div>

        {/* Header */}
        <header className="mb-6">
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' }}
            >
              <Notebook className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                Aide-mémoire moniteur · Plateau A1 / A2
              </p>
              <h1
                className="text-2xl sm:text-3xl font-black leading-tight mt-0.5"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Les 12 exercices plateau officiels
              </h1>
              <p className="text-sm text-muted-foreground mt-2 leading-snug">
                Fiche terrain mobile-first : titres, critères-clés observés, allures et REMC associés. <br className="hidden sm:inline" />
                Coche chaque critère validé pour suivre la progression de l&apos;élève.
              </p>
            </div>
          </div>
        </header>

        {/* Filtre catégories */}
        <div className="mb-5">
          <CategoryFilter active={filter} onChange={setFilter} />
        </div>

        {/* Liste exercices */}
        <div className="space-y-3" data-testid="exercices-list">
          {filtered.map((ex) => (
            <ExerciceCard key={ex.id} exercice={ex} />
          ))}
        </div>

        {/* Glossaire */}
        <section className="mt-8" data-testid="glossaire-section">
          <Accordion type="single" collapsible>
            <AccordionItem value="glossaire" className="rounded-2xl border-2 border-border px-4">
              <AccordionTrigger className="text-sm font-black uppercase tracking-wider">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-muted-foreground" /> Glossaire moniteur
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 py-2">
                  {GLOSSAIRE_PLATEAU.map((g) => (
                    <li key={g.abrev} className="flex items-start gap-3 text-sm">
                      <span className="inline-flex items-center justify-center min-w-[60px] h-6 rounded-md bg-muted text-foreground font-mono font-bold text-[11px]">
                        {g.abrev}
                      </span>
                      <span className="flex-1 text-muted-foreground leading-snug">{g.sens}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] italic text-muted-foreground mt-2">
                  Certaines abréviations sont issues des notes terrain manuscrites du moniteur ECSR.
                  Les définitions exactes peuvent évoluer selon le tracé plateau de chaque centre.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* Footer source */}
        <p className="text-[11px] italic text-muted-foreground text-center pt-6">
          Source : notes terrain moniteur ECSR · 12 exercices conformes au tracé officiel des épreuves plateau A1 / A2.
        </p>
      </div>
    </div>
  );
}
